import React, { useState, useEffect } from 'react';
import {
  adminGetContactMessages,
  adminUpdateContactMessageStatus,
  adminDownloadContactCsv,
} from '../services/adminApi';
import { Download, Archive, Mail, MailOpen, Inbox } from 'lucide-react';

const STATUS_FILTERS = [
  { key: 'all', label: 'All', icon: Inbox },
  { key: 'new', label: 'New', icon: Mail },
  { key: 'read', label: 'Read', icon: MailOpen },
  { key: 'archived', label: 'Archived', icon: Archive },
];

const statusBadgeClass = (status) => {
  switch (status) {
    case 'new': return 'admin-status-badge admin-status-new';
    case 'read': return 'admin-status-badge admin-status-read';
    case 'archived': return 'admin-status-badge admin-status-archived';
    default: return 'admin-status-badge';
  }
};

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const data = await adminGetContactMessages();
      setMessages(data);
    } catch (err) {
      console.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (messageId, newStatus) => {
    setUpdatingId(messageId);
    try {
      const updated = await adminUpdateContactMessageStatus(messageId, newStatus);
      setMessages((prev) => prev.map((m) => (m.id === messageId ? updated : m)));
    } catch (err) {
      alert('Failed to update message status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await adminDownloadContactCsv();
    } catch (err) {
      alert('CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) return <div data-testid="messages-loading">Loading...</div>;

  const visible = filter === 'all' ? messages : messages.filter((m) => m.status === filter);
  const counts = messages.reduce((acc, m) => {
    acc.all++;
    acc[m.status] = (acc[m.status] || 0) + 1;
    return acc;
  }, { all: 0, new: 0, read: 0, archived: 0 });

  return (
    <div data-testid="admin-messages">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <h2 className="admin-section-title" style={{ margin: 0 }}>
          Contact Messages ({counts.all})
        </h2>
        <button
          className="admin-button"
          onClick={handleExport}
          disabled={exporting || counts.all === 0}
          data-testid="export-csv-btn"
        >
          <Download size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <div className="admin-filter-chips" data-testid="message-filters">
        {STATUS_FILTERS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            className={`admin-filter-chip ${filter === key ? 'active' : ''}`}
            onClick={() => setFilter(key)}
            data-testid={`filter-${key}`}
          >
            <Icon size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
            {label} ({counts[key] || 0})
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="admin-card">
          <p>No messages in this view.</p>
        </div>
      ) : (
        <div className="admin-messages-list">
          {visible.map((message) => (
            <div
              key={message.id}
              className="admin-message-item"
              data-testid={`message-${message.id}`}
              data-status={message.status}
            >
              <div className="admin-message-header">
                <div className="admin-message-info">
                  <h3>{message.name}</h3>
                  <p>{message.email}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span className={statusBadgeClass(message.status)}>{message.status}</span>
                  <span className="admin-message-date">{formatDate(message.created_at)}</span>
                </div>
              </div>
              <div className="admin-message-subject">{message.subject}</div>
              <div className="admin-message-text">{message.message}</div>

              <div className="admin-message-actions">
                {message.status !== 'read' && (
                  <button
                    className="admin-button admin-button-secondary admin-button-sm"
                    onClick={() => setStatus(message.id, 'read')}
                    disabled={updatingId === message.id}
                    data-testid={`mark-read-${message.id}`}
                  >
                    <MailOpen size={14} style={{ marginRight: 4, verticalAlign: -2 }} />Mark Read
                  </button>
                )}
                {message.status !== 'new' && (
                  <button
                    className="admin-button admin-button-secondary admin-button-sm"
                    onClick={() => setStatus(message.id, 'new')}
                    disabled={updatingId === message.id}
                    data-testid={`mark-new-${message.id}`}
                  >
                    <Mail size={14} style={{ marginRight: 4, verticalAlign: -2 }} />Mark Unread
                  </button>
                )}
                {message.status !== 'archived' && (
                  <button
                    className="admin-button admin-button-secondary admin-button-sm"
                    onClick={() => setStatus(message.id, 'archived')}
                    disabled={updatingId === message.id}
                    data-testid={`archive-${message.id}`}
                  >
                    <Archive size={14} style={{ marginRight: 4, verticalAlign: -2 }} />Archive
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
